// TODO: Add action option (?)

import * as React from 'react';
import { defineMessages, IntlProvider, useIntl } from '@atlas/text';
import styled, { css, keyframes } from 'styled-components';

import Button from '@atlas/uicorn/lib/Control/Button';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import CrossIcon from '@atlas/uicorn/lib/Icon/CrossIcon';

import type { TExample } from '@atlas/uicorn/preview/modules';

export const messages = defineMessages({
    apply: { id: 'actApply', defaultMessage: 'Apply' },
    hide: { id: 'actHide', defaultMessage: 'Hide' },
});

const Alert = React.memo(
    React.forwardRef(function Alert(props: TAlertProps, ref: React.ForwardedRef<TAlertRef>): React.ReactElement {
        const { message, icon, danger, inline, actionText, onAction, onClose, topGap, ...elementProps } = props;
        const intl = useIntl();
        const dangerLevel = typeof danger === 'string' ? danger : danger ? 'high' : 'low';

        return (
            <SAlert {...elementProps} $topGap={topGap} $dangerLevel={dangerLevel} $inline={inline ?? false} ref={ref}>
                <SFigure>{icon || (dangerLevel === 'high' ? '❗' : dangerLevel === 'mid' ? '⚠️' : 'ℹ️')}</SFigure>
                {message}
                {onAction && (
                    <SActionButton onClick={onAction}>{actionText ?? intl.formatMessage(messages.apply)}</SActionButton>
                )}
                {onClose && (
                    <SCloseIcon
                        tooltip={intl.formatMessage(messages.hide)}
                        onClick={onClose}
                        icon={<CrossIcon size={16} />}
                    />
                )}
            </SAlert>
        );
    }),
);

export default Alert;

// #region Styles

const SCloseIcon = styled(IconButton)`
    color: ${(p) => p.theme.color.grey8};
    margin-left: auto;
`;

const SActionButton = styled(Button).attrs({ type: 'plain', noHoverStyle: true, color: 'primary' })`
    margin-left: auto;

    & + ${SCloseIcon} {
        margin-left: 0;
    }
`;

const SFigure = styled.figure`
    margin: 0;
`;

const appearanceKeyframes = keyframes`
    to {
        opacity: 1;
        translate: 0% 0%;
        scale: 1 1;
    }
`;

export const SAlert = styled.div<{ $dangerLevel: TAlertDangerLevel; $inline: boolean; $topGap?: number | boolean }>`
    display: ${(p) => (p.$inline ? 'inline-flex' : 'flex')};
    align-items: center;
    gap: 0.8rem;
    background: ${(p) =>
        p.$dangerLevel === 'high' ? '#f73c3c1f' : p.$dangerLevel === 'mid' ? '#ffa5001f' : '#0000ff1f'};
    color: ${(p) => p.theme.color.grey9};
    padding: 0.3rem 1.2rem;
    border-radius: ${(p) => p.theme.radius.small};
    min-height: 2.8rem;

    // TODO: remove specific reassignment after the base one is set correctly across the document
    font: ${(p) => p.theme.font.normal};

    opacity: 0;
    translate: 0% -30%;
    scale: 1 0.8;
    animation: ${appearanceKeyframes} 0.3s ease forwards;

    ${(p) =>
        p.$topGap
            ? css`
                  margin-top: ${Number(p.$topGap)}rem;
              `
            : ''}
`;

// #endregion

// #region Types

export type TAlertRef = HTMLDivElement;

export type TAlertDangerLevel = 'mid' | 'high' | 'low';

export type TAlertProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> & {
    message: React.ReactNode;
    icon?: React.ReactNode;
    danger?: boolean | TAlertDangerLevel;
    topGap?: number | boolean;
    inline?: boolean;
    actionText?: string;
    onAction?: () => void;
    onClose?: () => void;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <Alert
                message={props.message}
                icon={props.icon}
                danger={props.danger}
                inline={props.inline}
                onClose={props.closable ? props.onClose : undefined}
            />
        );
    },
    controls: {
        message: {
            type: 'string',
            initial: 'Use me to tell users about changes and new features',
            required: true,
        },
        icon: {
            type: 'string',
            initial: '📪',
            connected: false,
        },
        danger: 'boolean',
        inline: 'boolean',
        closable: { type: 'boolean', label: 'onClose', initial: true, required: true },
    },
    handlers: {
        onClose: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    // Tip: Read a little about testing library https://testing-library.com/docs/react-testing-library/example-intro

    const { describe, test, expect } = import.meta.vitest;
    describe('Alert', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        test('matches snapshot', async () => {
            const result = render(
                <IntlProvider locale="en">
                    <Example.render message="Welcome!" onClose={() => {}} />
                </IntlProvider>,
            );
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
